import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs/internal/Observable';
import { distinctUntilChanged, startWith, take } from 'rxjs/operators';
import { ActivityMultiSelectListContent } from 'src/app/shared/interfaces/activity-multi-select-list-content';
import { DrugsService } from 'src/app/shared/services/drugs.service';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { SearchInputColorEnum } from '../../../shared/components/search-input/search-input.component';
import { BaseTableDirective } from '../../../shared/directives/base-table.directive';
import { getFlatObject, groupByProperty, sortByProperty } from '../../../shared/helpers/helpers';
import { ActivityTypesEnum } from '../../../shared/interfaces/ActivityTypesEnum';
import { BadgeColorsEnum } from '../../../shared/interfaces/BadgeColorsEnum';
import { IDrug } from '../../../shared/interfaces/drug.interface';
import { GetDrugsForDrugsFilter } from '../../../shared/states/drugs/drugs.actions';
import { DrugsState } from '../../../shared/states/drugs/drugs.state';
import {
  RunCurrentInventoryReport,
  SaveCurrentInventoryReportFormState,
  SetCurrentInventoryReportRecords,
  SetLoading
} from '../../../shared/states/reports/reports.actions';
import { ReportsState } from '../../../shared/states/reports/reports.state';
import { ICurrentInventoryReportNdcModel } from '../../interfaces/current-inventory-report-ndc-model.interface';
import { ReportsService } from '../../reports.service';
import { GroupedBy } from 'src/app/shared/interfaces/grouped-by.enum';
import { groupByGPI } from '../../helpers';

@UntilDestroy()
@Component({
  selector: 'app-current-inventory-report',
  templateUrl: './current-inventory-report.component.html',
  styleUrls: ['./current-inventory-report.component.scss'],
})
export class CurrentInventoryReportComponent extends BaseTableDirective implements OnInit {
  filtersForm: UntypedFormGroup;
  reportsTableControlsForm: UntypedFormGroup;

  tableColumns = {
    friendlyName: true,
    locationName: true,
    currentInventoryCount: true,
  };

  originalRecordsList: ICurrentInventoryReportNdcModel[] = [];
  sortedRecordsList: any[] = [];
  groupItemsOpened: boolean[] = [];

  isReportRunApplied: boolean;

  drugItemsShowLimit = 2;

  isDrugsDropdownOpened: boolean;
  isFileDownloading: boolean;

  drugsList = [];

  originalTitle = 'Current On-Hand Report';
  title = this.originalTitle;

  activitiesList = ActivityMultiSelectListContent;

  drugListSettings = {
    selectAllText: 'All Drugs',
    unSelectAllText: 'Deselect All',
    allowSearchFilter: true,
    searchPlaceholderText: 'Search Drugs',
    noDataAvailablePlaceholderText: 'No drugs available',
  };


  filterResultsBy: 'drug' | 'location' | 'gpi' = 'drug';

  SearchInputColorEnum = SearchInputColorEnum;
  ActivityTypesEnum = ActivityTypesEnum;
  BadgeColorsEnum = BadgeColorsEnum;

  @Select(ReportsState.loading) isLoading$: Observable<boolean | undefined>;
  @Select(ReportsState.currentInventoryRecords) currentInventoryRecords$: Observable<ICurrentInventoryReportNdcModel[]>;
  @Select(ReportsState.runningReports) runningReports$: Observable<boolean>;
  @Select(ReportsState.currentInventoryReportForm) currentInventoryReportForm$: Observable<any>;
  @Select(DrugsState.drugsFilterDrugItems) drugsFilterDrugItems$: Observable<IDrug[]>;
  @Select(PharmacyState.enableC3toC5Drugs) enableC3toC5Drugs$: Observable<boolean>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;

  @ViewChild('drugsMultiOptionalDropdown') drugsMultiOptionalDropdown;
  @ViewChild('downloadMenu') downloadMenu;
  @ViewChild('tbody') tbody;

  private schedulesInReport: number[];

  inventoryLocationsEnabled: boolean;

  constructor(
    protected store: Store,
    private fb: UntypedFormBuilder,
    private reportsService: ReportsService,
    private drugService: DrugsService
  ) {
    super(store);

    this.filtersForm = this.fb.group({
      drugs: [[], []],
      schedules: null,
      inventoryLocations: null
    });

    this.reportsTableControlsForm = this.fb.group({
      searchValue: ['', []],
    });

    if (!this.store.selectSnapshot(PharmacyState.inventoryLocationsEnabled)) {
      this.activitiesList = this.activitiesList.filter(x => x.id !== ActivityTypesEnum.MovedFrom && x.id !== ActivityTypesEnum.MovedTo);
    }

    this.inventoryLocationsEnabled$
    .pipe(untilDestroyed(this))
    .subscribe(x => this.inventoryLocationsEnabled = x);
  }

  ngOnInit(): void {
    this.currentInventoryRecords$.pipe(untilDestroyed(this))
      .subscribe((records: ICurrentInventoryReportNdcModel[]) => {
        if (records && records.length) {
          this.originalRecordsList = records;
          this.filterBySearchValue(this.reportsTableControlsForm.get('searchValue').value);
        } else {
          this.originalRecordsList = [];
        }
        this.schedulesInReport = this.drugService.getSchedulesBasedOnExistingInventories(this.originalRecordsList as any)
        this.changeTitle(this.schedulesInReport || []);
      });

    this.store.dispatch(new GetDrugsForDrugsFilter());

    this.drugsFilterDrugItems$.pipe(untilDestroyed(this))
      .subscribe((data: IDrug[]) => {
        this.populateDrugFilter(data, this.filtersForm.value?.schedules?.map(x => x.id));
      });

    //restore filters
    this.currentInventoryReportForm$
    .pipe(take(1))
    .subscribe(data => {
        this.filtersForm.patchValue(data);
      });

    this.reportsTableControlsForm.get('searchValue')?.valueChanges
      .pipe(
        untilDestroyed(this),
        startWith(''),
        distinctUntilChanged(),
      )
      .subscribe((search: string) => {
        this.filterBySearchValue(search);
      });

    this.filtersForm.get('schedules')?.valueChanges
    .pipe(
      untilDestroyed(this),
    )
    .subscribe((schedules: any) => {
      this.populateDrugFilter(this.store.selectSnapshot(DrugsState)?.drugsFilterDrugItems, schedules?.map(x => x.id));
    });

    this.enableC3toC5Drugs$.subscribe(enabled => {
      if (!enabled) {
        this.filtersForm.get('schedules').reset();
      }
    });
  }

  changeTitle(existingSchedules: number[]): void {
    this.title = this.reportsService.composeReportTitleWithScheduleExtension(this.originalTitle, [...existingSchedules || []]);
  }

  focusMultioptionalDropdown(dropdownName: string): void {
    setTimeout(() => {
      this[dropdownName].opened = true;
      this[dropdownName].isFocused = true;
      this[dropdownName].dropDownOpened.emit();
      this[dropdownName].multiselectDropdown.nativeElement.focus();
    });
  }

  updateSortedList(source: ICurrentInventoryReportNdcModel[]): void {
    const flatList = [...source].map((transaction: ICurrentInventoryReportNdcModel) => {
      const flatTransaction = getFlatObject({...transaction, history: null});
      switch (this.filterResultsBy) {
        case 'location':
          flatTransaction.location = `${flatTransaction.locationName}`;
          break;
        case 'drug':
          flatTransaction.drug = `${flatTransaction['friendlyName']} (${flatTransaction['packageNdc']})`;
          break;
        case 'gpi':
          flatTransaction.gpi = flatTransaction['gpi'];
          break; 
      }

      return flatTransaction;
    });
    setTimeout(() => {
      this.sortedRecordsList = this.filterResultsBy === 'gpi' ? groupByGPI(flatList, 'currentInventoryCount') : groupByProperty(flatList, this.filterResultsBy, 'currentInventoryCount');
      this.groupItemsOpened = Array(this.sortedRecordsList.length).fill(true);
      this.store.dispatch(new SetLoading(false));
    });
  }

  reset(): void {
    this.filtersForm.reset();
    this.reportsTableControlsForm.get('searchValue').setValue('');
    this.filtersForm.get('schedules').setValue([]);
    this.store.dispatch(new SaveCurrentInventoryReportFormState(null));
    this.originalRecordsList = [];
    this.store.dispatch(new SetCurrentInventoryReportRecords([]));
  }

  runReport(): void {
    const raw = this.filtersForm.getRawValue();
    this.store.dispatch(new SaveCurrentInventoryReportFormState(this.filtersForm.getRawValue()));
    this.store.dispatch(new RunCurrentInventoryReport({
      drugIds: raw?.drugs?.map(x => x.id) || [],
      schedules: raw?.schedules?.map(x => x.id),
      locationIds: raw?.inventoryLocations?.map(x => x.id)
    }));
    this.isReportRunApplied = true;
  }

  saveCSV(): void {
    this.store.dispatch(new SetLoading(true));
    this.isFileDownloading = true;
    this.reportsService.downloadCurrentInventoryCsvReport(
      {
        records: this.getEntitiesToExport(), 
        withLocation: this.inventoryLocationsEnabled,
        groupedBy: this.getGroupedBy()
      }
    )
      .pipe(take(1))
      .subscribe(res => {
        const name = this.title + ' from ' + new Date().toDateString() + '.csv';
        this.handleDownload(res, name);
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      }, err => {
        this.store.dispatch(new SetLoading(false));
        this.isFileDownloading = false;
      });
  }
  
  savePDF(): void {
    this.store.dispatch(new SetLoading(true));
    this.isFileDownloading = true;
    this.reportsService.downloadCurrentInventoryPdfReport({
      title: this.title,
      records: this.getEntitiesToExport(),
      withLocation: this.inventoryLocationsEnabled,
      groupedBy: this.getGroupedBy()
    })
    .pipe(take(1))
    .subscribe(res => {
      const name = this.title + ' from ' + new Date().toDateString() + '.pdf';
      this.handleDownload(res, name);
      this.store.dispatch(new SetLoading(false));
      this.isFileDownloading = false;
    }, err => {
      this.store.dispatch(new SetLoading(false));
      this.isFileDownloading = false;
    });
  }


  getEntitiesToExport() {
    const groups =  this.sortedRecordsList.map(group => group.records.map(x => {
      return {
        friendlyName: x['friendlyName'],
        packageNdc: x['packageNdc'],
        locationName: x?.locationName,
        currentInventoryCount: x.currentInventoryCount,
        similarOnHand: x.similarOnHand,
        gpi: x.gpi
      }})
    );

    const merged = [];
    groups.forEach(array => {
      merged.push(...array);
    });

    return merged;
  }


  handleDownload(res: Blob, fileName: string): void {
    const downloadedFile = new Blob([res], {type: res.type});
    const a = document.createElement('a');
    a.setAttribute('style', 'display:none;');
    document.body.appendChild(a);
    a.download = fileName;
    a.href = URL.createObjectURL(downloadedFile);
    a.target = '_blank';
    a.click();
    document.body.removeChild(a);
  }

  areFiltersEmpty(): boolean {
    const formValue = this.filtersForm.getRawValue();
    return Object.values(formValue).every((x: any) => !x || (Array.isArray(x) && !x.length));
  }

  multiselectOpened(limitName: string, dropdownShowName: string): void {
    setTimeout(() => {
      this.drugItemsShowLimit = 1;
      this.isDrugsDropdownOpened = false;
      this[limitName] = 4;
      this[dropdownShowName] = true;
    });
  }

  multiselectClosed(limitName: string, dropdownShowName: string): void {
    this.drugItemsShowLimit = 2;
    this[dropdownShowName] = false;
  }

  switchFilterResultBy(filterBy: 'location' | 'drug' | 'gpi'): void {
    this.filterResultsBy = filterBy;
    this.updateSortedList(this.originalRecordsList);
    this.filterBySearchValue(this.reportsTableControlsForm.get('searchValue').value);
  }

  sortByColumn(columnName: string): void {
    this.store.dispatch(new SetCurrentInventoryReportRecords(this.sort(this.originalRecordsList, columnName)));
  }


  filterBySearchValue(searchString: string = ''): void {
    searchString = searchString?.replace(/-/g, '');
    const lowerCaseSearchStr = searchString?.toLocaleLowerCase();
    const filterFunction = (search, data) => searchString ?
      data.filter(el => 
        el.rxNumber?.toLowerCase().includes(lowerCaseSearchStr) ||
        el.patientName?.toString().toLowerCase().includes(lowerCaseSearchStr) ||
        el.friendlyName.toLowerCase().toString().includes(lowerCaseSearchStr) ||
        el.packageNdc.replace(/-/g, '').includes(search)) : data;
    const filtered = filterFunction(searchString, [...this.originalRecordsList]);
    this.updateSortedList(filtered);
  }

  private populateDrugFilter(drugs: IDrug[], schedules: number[]): void {
    if (this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs)) {
      schedules = !schedules?.length ? this.drugService.getSchedulesList() : schedules;
    }
    let filteredBySchedule = [];
    if (this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs)) {
      filteredBySchedule = drugs?.filter(x => schedules?.includes(x.schedule)) || [];
    } else filteredBySchedule = drugs || [];

    this.drugsList = filteredBySchedule.map(el => ({id: el.id, name: el.friendlyName, itemName: el.friendlyName, code: el.packageNdc, schedule: el.schedule}));
    this.drugsList = sortByProperty(this.drugsList, 'name', true);
  }

  private getGroupedBy(): GroupedBy {
    switch (this.filterResultsBy) {
      case 'drug':
        return GroupedBy.Drug;
      case 'location':
        return GroupedBy.Location;
      case 'gpi':
        return GroupedBy.GPI;
    }
  }
 
  trackByFn(index, item): number | string {
    return item.id;
  }
}
